import { formatUSDC } from "../hooks/useGames";

// v2 balance-card sparkline shapes. Illustrative only (direction, not a real
// balance series): SPARK_UP rises for a 7-day gain, SPARK_DOWN declines
// gradually for a loss. The line/fill colour follows the P&L sign.
const SPARK_UP =
  "M 0 38 L 22 36 L 44 32 L 66 38 L 88 30 L 110 33 L 132 26 L 154 30 L 176 24 L 198 20 L 220 26 L 242 18 L 264 14 L 286 12 L 320 8";
const SPARK_DOWN =
  "M 0 10 L 22 12 L 44 16 L 66 12 L 88 18 L 110 16 L 132 22 L 154 20 L 176 26 L 198 30 L 220 26 L 242 34 L 264 38 L 286 42 L 320 48";

interface BalanceDeltaProps {
  /** Net 7-day P&L, USDC base units (6-dec). Signed. Real — summed from the
   *  user's settled on-chain matches. */
  pnlMinor: bigint;
  /** Current wallet balance, USDC base units (live on-chain). The % is the
   *  P&L as a fraction of this. */
  balanceMinor: bigint;
}

/**
 * The wallet card's "+$X · +Y% · LAST 7 DAYS" delta + sparkline. Both numbers
 * are real and measured: `pnlMinor` is the net P&L of settled on-chain matches
 * over the window, and the percentage is that P&L over the *current* on-chain
 * balance (no estimated/simulated historical balance). Green + rising when up,
 * red + gradually declining on a loss; the sparkline shape is illustrative
 * (direction only). Relies on the `.wc-*` styles injected by WalletPanel.
 */
export default function BalanceDelta({ pnlMinor, balanceMinor }: BalanceDeltaProps) {
  const up = pnlMinor >= 0n;
  const abs = up ? pnlMinor : -pnlMinor;
  // % = P&L as a fraction of the current (real) balance — no estimation.
  const pct = balanceMinor > 0n ? (Number(pnlMinor) / Number(balanceMinor)) * 100 : 0;
  const sign = up ? "+" : "−";
  const amountStr = `${sign}$${formatUSDC(abs)}`;
  const pctStr = `${sign}${Math.abs(pct).toFixed(2)}% · LAST 7 DAYS`;
  const color = up ? "#5fcf80" : "#ff5a5a";
  const glow = up ? "rgba(95,207,128,0.45)" : "rgba(255,90,90,0.45)";
  const path = up ? SPARK_UP : SPARK_DOWN;
  const dotY = up ? 8 : 48;

  return (
    <>
      <div className="wc-delta">
        <span className={up ? "wc-delta-badge" : "wc-delta-badge neg"}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
            <path
              d={up ? "M7 14l5-5 5 5" : "M7 10l5 5 5-5"}
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {amountStr}
        </span>
        <span className="wc-delta-sub">{pctStr}</span>
      </div>
      <div className="wc-spark-wrap">
        <svg width="100%" height="56" viewBox="0 0 320 56" preserveAspectRatio="none" style={{ display: "block" }}>
          <defs>
            <linearGradient id="wc-spark-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.35" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <line x1="0" y1="28" x2="320" y2="28" stroke="rgba(245,239,230,0.05)" strokeDasharray="2 4" />
          <path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: `drop-shadow(0 0 6px ${glow})` }}
          />
          <path d={`${path} L 320 56 L 0 56 Z`} fill="url(#wc-spark-grad)" />
          <circle cx="320" cy={dotY} r="4" fill={color} style={{ filter: `drop-shadow(0 0 8px ${color})` }} />
          <circle cx="320" cy={dotY} r="8" fill={color} opacity="0.18" />
        </svg>
      </div>
      <div className="wc-spark-labels">
        <span>7 DAYS AGO</span>
        <span>TODAY</span>
      </div>
    </>
  );
}
